import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useGameStore, useIsHost } from '../stores/gameStore';
import { useGameActions, useSocket } from '../hooks/useSocket';
import { WAITING_MESSAGES } from '@youraiquiz/shared';

const CategoryInputView = () => {
  const { gameState, clientState } = useGameStore();
  const isHost = useIsHost();
  const { submitCategories, kickPlayer } = useGameActions();
  const { leaveRoom } = useSocket();

  const categoriesCount = gameState.config.categoriesPerPlayer || 2;
  const [cats, setCats] = useState<string[]>(Array(categoriesCount).fill(''));
  const [msgIndex, setMsgIndex] = useState(0);
  const [confirmKickId, setConfirmKickId] = useState<string | null>(null);

  // Also check if status is still CATEGORY_INPUT - prevents flash of input form during transition
  const myPlayer = gameState.players.find(p => p.id === clientState.myId);
  const alreadySubmitted = myPlayer?.hasVoted || gameState.status !== 'CATEGORY_INPUT';
  const waitingFor = gameState.players.filter(p => !p.hasVoted);
  const readyCount = gameState.players.length - waitingFor.length;

  // Adjust cats array if config changes
  useEffect(() => {
    setCats(prev => {
      if (prev.length === categoriesCount) return prev;
      const newCats = [...prev];
      if (newCats.length < categoriesCount) {
        return [...newCats, ...Array(categoriesCount - newCats.length).fill('')];
      }
      return newCats.slice(0, categoriesCount);
    });
  }, [categoriesCount]);

  // Rotate waiting messages
  useEffect(() => {
    if (alreadySubmitted) {
      const interval = setInterval(() => {
        setMsgIndex(prev => (prev + 1) % WAITING_MESSAGES.length);
      }, 3000);
      return () => clearInterval(interval);
    }
  }, [alreadySubmitted]);

  const handleKickClick = (id: string) => {
    if (confirmKickId === id) {
      kickPlayer(id);
      setConfirmKickId(null);
    } else {
      setConfirmKickId(id);
      setTimeout(() => setConfirmKickId(null), 3000);
    }
  };

  const handleSubmit = () => {
    if (cats.every(c => c.trim().length > 1)) {
      submitCategories(cats.map(c => c.trim()));
    }
  };

  // Waiting screen after submission
  if (alreadySubmitted) {
    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.2 }}
        className="w-full max-w-2xl flex flex-col items-center space-y-12"
      >
        {/* Leave Room Button */}
        <button
          onClick={leaveRoom}
          className="self-start text-[10px] font-black uppercase tracking-widest text-white/30 hover:text-red-500 transition-colors"
        >
          LEAVE ROOM
        </button>

        <div className="relative">
          <motion.div
            animate={{ rotate: [0, 360], scale: [1, 1.1, 1] }}
            transition={{ duration: 10, repeat: Infinity, ease: 'linear' }}
            className="absolute -inset-16 bg-gradient-to-r from-neon-purple/20 to-neon-blue/20 blur-[60px] rounded-full"
          />
          <div className="relative flex gap-8 text-7xl">
            <motion.span animate={{ y: [0, -20, 0] }} transition={{ duration: 2, repeat: Infinity, delay: 0 }}>🍹</motion.span>
            <motion.span animate={{ y: [0, -20, 0] }} transition={{ duration: 2, repeat: Infinity, delay: 0.5 }}>🍸</motion.span>
            <motion.span animate={{ y: [0, -20, 0] }} transition={{ duration: 2, repeat: Infinity, delay: 1 }}>🍺</motion.span>
          </div>
        </div>

        <div className="text-center space-y-4">
          <h2 className="text-6xl font-black italic tracking-tighter uppercase">
            THE <span className="text-neon-purple">LOUNGE</span>
          </h2>
          <div className="h-8">
            <AnimatePresence mode="wait">
              <motion.p
                key={msgIndex}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                className="text-xl font-bold text-gray-400 italic"
              >
                {WAITING_MESSAGES[msgIndex]}
              </motion.p>
            </AnimatePresence>
          </div>
        </div>

        {/* Progress bar */}
        <div className="w-full space-y-6">
          <div className="flex justify-between items-end px-2">
            <span className="text-xs font-black uppercase tracking-[0.3em] text-neon-blue">Orders Received</span>
            <span className="text-2xl font-black">{readyCount} / {gameState.players.length}</span>
          </div>
          <div className="w-full h-4 bg-white/5 rounded-full p-1 border border-white/10 overflow-hidden">
            <motion.div
              initial={{ width: 0 }}
              animate={{ width: `${(readyCount / gameState.players.length) * 100}%` }}
              className="h-full bg-gradient-to-r from-neon-blue to-neon-purple rounded-full shadow-[0_0_20px_rgba(6,182,212,0.5)]"
            />
          </div>
        </div>

        {/* Player status grid */}
        <div className="w-full glass p-8 rounded-[2rem] border-b-8 border-white/10">
          <h3 className="text-center text-[10px] font-black uppercase tracking-[0.5em] text-white/30 mb-8">Service Status</h3>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-6">
            {gameState.players.map(p => {
              const isConfirming = confirmKickId === p.id;
              const isMe = p.id === clientState.myId;
              return (
                <motion.div
                  key={p.id}
                  layout
                  className={`flex flex-col items-center gap-2 p-4 rounded-2xl transition-all relative ${
                    p.hasVoted
                      ? 'bg-green-500/10 border border-green-500/30'
                      : 'bg-white/5 border border-white/5'
                  } ${isConfirming ? 'border-red-600 bg-red-600/10' : ''}`}
                >
                  {isHost && !isMe && (
                    <button
                      type="button"
                      onClick={() => handleKickClick(p.id)}
                      className={`absolute -top-1 -right-1 h-5 px-1 rounded-full flex items-center justify-center font-black text-[7px] shadow-lg z-10 transition-all ${
                        isConfirming ? 'bg-red-600 text-white w-auto scale-110' : 'bg-red-600 text-white w-5 hover:scale-110'
                      }`}
                    >
                      {isConfirming ? 'KICK?' : '✕'}
                    </button>
                  )}
                  <div className="relative">
                    <span className={`text-4xl transition-all ${p.hasVoted ? 'grayscale-0' : 'grayscale brightness-50'}`}>
                      {p.avatar}
                    </span>
                    {p.hasVoted && (
                      <motion.div
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        className="absolute -top-2 -right-2 bg-green-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-[10px] font-bold"
                      >
                        ✓
                      </motion.div>
                    )}
                  </div>
                  <span className={`text-[10px] font-black uppercase truncate w-full text-center ${p.hasVoted ? 'text-green-500' : 'text-white/40'}`}>
                    {p.name}
                  </span>
                </motion.div>
              );
            })}
          </div>
        </div>
      </motion.div>
    );
  }

  // Category input form
  return (
    <motion.div
      initial={{ opacity: 0, x: 50 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.2 }}
      className="w-full max-w-lg space-y-6"
    >
      {/* Leave Room Button */}
      <button
        onClick={leaveRoom}
        className="text-[10px] font-black uppercase tracking-widest text-white/30 hover:text-red-500 transition-colors"
      >
        LEAVE ROOM
      </button>

      <div className="glass p-8 rounded-3xl space-y-8">
        <div className="text-center">
          <h2 className="text-4xl font-black mb-2 italic tracking-tighter uppercase">
            PICK YOUR <span className="text-neon-blue">POISON</span>
          </h2>
          <p className="text-gray-400 font-bold uppercase text-xs tracking-widest">
            Input {categoriesCount} Trivia Categories
          </p>
        </div>

        <div className="space-y-4">
          {cats.map((cat, i) => (
            <input
              key={i}
              value={cat}
              onChange={(e) => {
                const newCats = [...cats];
                newCats[i] = e.target.value;
                setCats(newCats);
              }}
              placeholder={`Category #${i + 1} (e.g. 90s Pop)`}
              maxLength={50}
              className="w-full bg-white/5 border-2 border-white/10 p-5 rounded-2xl focus:border-neon-blue outline-none transition-all text-xl font-bold placeholder:text-white/10"
            />
          ))}
        </div>

        <button
          onClick={handleSubmit}
          disabled={!cats.every(c => c.trim().length > 1)}
          className="w-full bg-neon-blue p-6 rounded-2xl font-black text-2xl hover:scale-[1.02] active:scale-95 transition-all shadow-lg shadow-neon-blue/20 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          SUBMIT CATEGORIES
        </button>
      </div>

    </motion.div>
  );
};

export default CategoryInputView;
